import { BreadcrumbList, ListItem, Offer, Product, PropertyValue, WithContext } from "schema-dts";

import { ProductEntity, ProductStockStatus, SellerEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarSellerUrl, bazaarUrl } from "@/mk2/apps/bazaar/urls";
import { url } from "@/mk2/helpers/urls";
import { BAZAAR_SEO_BABY, CURRENCY_ISO } from "@/mk/autogenerated/translations/structuredData.3aad89248f3c52e32d10d1ddb045b733";
import { FACET_NAMES } from "@/mk/bazaar/common/enums";
import { LeafCategory, getLabel } from "@/mk/bazaar/product/categories";
import { productSlugToArray } from "@/mk/bazaar/product/utils";
import { photoUrl } from "@/mk/photo/photoUrl";
import {
    bazaar_single_page_item_detail_url,
    bazaar_single_page_product_grid_category_url,
} from "@/mk/urls/functions";

const breadcrumbBazaar = (baseUrl: string): ListItem => ({
    "@type": "ListItem",
    position: 1,
    item: {
        "@id": baseUrl + bazaarUrl,
        name: BAZAAR_SEO_BABY,
        // 'image': assetUrl + 'img/logos/ver-2/logo-' + settings.ACTIVE_SERVER_ID + '.png',
    },
});

const breadcrumbSeller = (baseUrl: string, seller: SellerEntity, position = 2): ListItem => ({
    "@type": "ListItem",
    position: position,
    item: {
        "@id": baseUrl + url(bazaarSellerUrl, { username: seller.user.username }),
        name: seller.user.username,
        image: seller.user.avatar,
    },
});

export function breadcrumbsSeller(
    baseUrl: string,
    seller: SellerEntity,
): WithContext<BreadcrumbList> {
    const items = [breadcrumbBazaar(baseUrl)];
    if (seller) {
        items.push(breadcrumbSeller(baseUrl, seller));
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

const breadcrumbCategory = (baseUrl: string, category: LeafCategory, position = 2): ListItem => ({
    "@type": "ListItem",
    position: position,
    item: {
        "@id": baseUrl + bazaar_single_page_product_grid_category_url(category.slug),
        name: getLabel(category.name),
        // 'image': assetUrl + 'img/logos/ver-2/logo-' + settings.ACTIVE_SERVER_ID + '.png',
    },
});

const breadcrumbProduct = (
    baseUrl: string,
    productData: ProductEntity,
    position = 3,
): ListItem => ({
    "@type": "ListItem",
    position: position,
    item: {
        "@id":
            baseUrl + bazaar_single_page_item_detail_url(...productSlugToArray(productData.slug)),
        name: productData.title,
        image: productData.photos?.[0]?.photofile
            ? photoUrl(productData.photos?.[0]?.photofile, "s221x221")
            : undefined,
    },
});

export function breadcrumbsProduct(
    baseUrl,
    category?: LeafCategory,
    productData?,
): WithContext<BreadcrumbList> {
    const items = [breadcrumbBazaar(baseUrl)];
    if (category) {
        items.push(breadcrumbCategory(baseUrl, category));
    }

    if (productData) {
        items.push(breadcrumbProduct(baseUrl, productData, category ? 3 : 2));
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function product(baseUrl, productData: ProductEntity): WithContext<Product> {
    const result: WithContext<Product> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: productData.title,
        image: productData.photos?.[0]?.photofile
            ? photoUrl(productData.photos?.[0]?.photofile, "s221x221")
            : undefined,
        description: productData.description || productData.seo.description,
        sku: productData.id.toString(),
        offers: {
            "@type": "Offer",
            priceCurrency: CURRENCY_ISO,
            price: productData.price,
            itemCondition:
                productData.quality === "old"
                    ? "https://schema.org/UsedCondition"
                    : "https://schema.org/NewCondition",
            seller: productData.seller?.user?.username
                ? {
                      "@type": "Person",
                      name: productData.seller.user.username,
                  }
                : undefined,
            url:
                baseUrl +
                bazaar_single_page_item_detail_url(...productSlugToArray(productData.slug)),
        },
        additionalProperty: [],
    };

    const brand = productData.properties.find((prop) => prop[0] === "p_brand");
    if (brand) {
        result.brand = {
            "@type": "Brand",
            name: brand[2],
        };
    }

    if (productData.stockStatus === ProductStockStatus.IN_STOCK) {
        (result.offers as Offer).availability = "https://schema.org/InStock";
    }

    // Additional Properties
    productData.properties.forEach((prop) => {
        // Ignore brand and cat
        if (["p_brand", "cat"].indexOf(prop[0]) !== -1) {
            return;
        }

        const msgId = `BZ2_FACET_${prop[0].toUpperCase()}`;
        (result.additionalProperty as PropertyValue[]).push({
            "@type": "PropertyValue",
            name: FACET_NAMES[msgId] || msgId,
            value: prop[2],
        });
    });

    return result;
}
